import type { ScanCode } from "@hobbyshopify/backend-client";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { barcodeProblem, barcodeToCode, codeOutcomeText } from "../../core/primitives/barcode";
import type { CardCamera } from "../../core/services/card-camera";
import type { Rect, Size } from "../../core/services/scan-image";
import type { ScanSession } from "../../core/services/scan-session";
import { ScanCardActions, ScanCardState, type ScanKindChoice } from "./scan-card-model";
import { BACK_PROMPT, CAMERA_SENTENCES, FRONT_PROMPT, importUnreadable, SHOT_UNREADABLE } from "./scanner";

/** The live camera as the scanner uses it: `CardCamera` in the app, a fake in tests. */
export type ScannerCamera = Pick<
  CardCamera,
  | "phase"
  | "failure"
  | "torch"
  | "attach"
  | "start"
  | "stop"
  | "setTorch"
  | "shot"
  | "addListener"
  | "removeListener"
  | "dispose"
>;

export type ScanCardDeps = Pick<AppDeps, "scanSessions" | "storeRepository" | "broadcastStudio"> & {
  camera: ScannerCamera;
  /** An import or a photo from the system camera, decoded and sized as a scan sends it; null when it cannot be read. */
  readPicture: (file: File) => Promise<Blob | null>;
  /** The shutter's feel, where the device has one. */
  vibrate?: () => void;
  /** A retail barcode in a picture, where the browser can read one. Absent reads none. */
  detectBarcode?: (picture: Blob) => Promise<string | null>;
};

export interface ScanCardRouting {
  /** Where the session lands: Review, or the customer's page. */
  done(): void;
}

/**
 * The scanner, as iOS's scan mode: a live camera with a card-shaped guide, a shutter that sends each card as it is
 * shot, imports, and a tray of the session's cards. The session outlives the page (`ScanSessions`); this page only
 * shows it and feeds it.
 */
export class ScanCardController extends SubtreeController implements ScanCardActions {
  readonly state = new ScanCardState();
  #session: ScanSession | null = null;
  /** The session this scanner opened and has not let go of yet. */
  #opened: ScanSession | null = null;
  #imports: Promise<void> = Promise.resolve();
  #queued = 0;
  #unreadable: string[] = [];
  #done = false;

  constructor(
    readonly deps: ScanCardDeps,
    readonly routing: ScanCardRouting,
    /** A counter customer scanned for, from their page; the scans are theirs. */
    readonly customerId: string | null = null,
  ) {
    super();
    this.subtree.put(ScanCardState, this.state);
    this.subtree.put(ScanCardActions, this);
    this.sync(() => this.#follow(), [deps.storeRepository]);
    this.sync(() => this.#showCamera(), [deps.camera, deps.broadcastStudio]);
    this.sync(() => {
      const { barcode, kind } = this.state;
      this.state.barcodeError.value = barcodeProblem(barcode.value);
      this.state.canLookUp.value = kind.value !== "card" && barcodeToCode(barcode.value) !== null;
    }, [this.state.barcode, this.state.kind]);
    this.autoDispose(() => {
      this.#session?.removeListener(this.#mirror);
      // Closing the scanner by any way out: what is ready leaves the tray.
      this.#letGo();
      deps.camera.dispose();
    });
  }

  cameraShown = (video: HTMLVideoElement | null) => {
    const { camera, broadcastStudio } = this.deps;
    camera.attach(video);
    if (video === null) {
      camera.stop();
      return;
    }
    // One camera: while Live holds it, the scanner does not take it.
    if (!broadcastStudio.onAir) void camera.start();
  };

  shoot = async (view: Size, guide: Rect) => {
    const { camera } = this.deps;
    if (this.#session === null || camera.phase !== "on") return;
    this.deps.vibrate?.();
    const shot = await camera.shot(view, guide);
    if (this.isDisposed) return;
    if (shot === null) {
      this.#say(SHOT_UNREADABLE);
      return;
    }
    this.state.notice.value = null;
    const codes = await this.#codesFor(shot);
    if (this.isDisposed) return;
    this.#session?.add(shot, codes);
  };

  importPictures = (files: readonly File[]): Promise<void> => {
    const session = this.#session;
    if (session === null || files.length === 0) return this.#imports;
    if (this.#queued++ === 0) {
      this.#unreadable = [];
      this.state.notice.value = null;
    }
    this.state.importing.value = true;
    // A pick made while another is read waits its turn, so fronts and backs still pair in the order chosen.
    const read = this.#imports.then(() => this.#read(session, files));
    this.#imports = read.catch(() => undefined);
    return read;
  };

  setFrontAndBack = (on: boolean) => {
    this.#session?.setFrontAndBack(on);
  };

  setKind = (kind: ScanKindChoice) => {
    this.#session?.setKind(kind === "auto" ? null : kind);
  };

  setBarcode = (value: string) => {
    this.state.barcode.value = value;
  };

  lookUpBarcode = () => {
    const code = barcodeToCode(this.state.barcode.value);
    if (this.#session === null || code === null || !this.state.canLookUp.value) return;
    this.state.barcode.value = "";
    this.#session.lookUp([code]);
  };

  retryBarcode = () => {
    const waiting = this.#session?.awaitingPhoto;
    if (waiting) this.#session?.retry(waiting.id);
  };

  discardHeldFront = () => {
    this.#session?.discardHeldFront();
  };

  toggleTorch = async () => {
    const { camera } = this.deps;
    if (camera.torch === null) return;
    await camera.setTorch(!camera.torch);
  };

  retry = (id: string) => {
    this.#session?.retry(id);
  };

  discard = (id: string) => {
    this.#session?.discard(id);
  };

  done = () => {
    if (this.#done) return;
    this.#done = true;
    this.#letGo();
    this.deps.camera.stop();
    this.routing.done();
  };

  #say(notice: string): void {
    this.state.notice.value = notice;
    this.state.noticeSaid.value += 1;
  }

  /** Lets go of the session once what was picked is read, so the pictures of a Front & back pick still pair in order. */
  #letGo(): void {
    const session = this.#opened;
    if (session === null) return;
    this.#opened = null;
    if (this.#queued === 0) session.closeScanner();
    else void this.#imports.then(() => session.closeScanner(true));
  }

  /**
   * One at a time and in the order picked. What was picked goes on to its session after the scanner closes, as iOS
   * goes on importing: the session outlives the page.
   */
  async #read(session: ScanSession, files: readonly File[]): Promise<void> {
    try {
      for (const file of files) {
        const picture = await this.deps.readPicture(file);
        if (picture === null) this.#unreadable.push(file.name);
        else session.add(picture, await this.#codesFor(picture));
      }
    } finally {
      if (--this.#queued === 0 && !this.isDisposed) {
        this.state.importing.value = false;
        if (this.#unreadable.length > 0) this.#say(importUnreadable(this.#unreadable.join(", ")));
      }
    }
  }

  /** The barcode typed, which goes with this picture alone, else the one read off it. */
  async #codesFor(picture: Blob): Promise<ScanCode[]> {
    const typed = barcodeToCode(this.state.barcode.value);
    if (typed !== null) {
      this.state.barcode.value = "";
      return [typed];
    }
    const read = await this.deps.detectBarcode?.(picture);
    const code = read ? barcodeToCode(read) : null;
    return code ? [code] : [];
  }

  /** The session of the shop and counter chosen now (and the customer), which the shell can change under the page. */
  #follow(): void {
    // StrictMode disposes a scanner before its first pass: it follows nothing then.
    if (this.isDisposed) return;
    const { shopId, locationId } = this.deps.storeRepository;
    const next =
      shopId === null ? null : this.deps.scanSessions.for({ shopId, locationId, customerId: this.customerId });
    if (next === this.#session) return;
    this.#session?.removeListener(this.#mirror);
    this.#letGo();
    this.#session = next;
    next?.addListener(this.#mirror);
    next?.openScanner();
    this.#opened = next;
    this.#mirror();
  }

  readonly #mirror = (): void => {
    const session = this.#session;
    this.state.captures.value = session?.captures ?? [];
    this.state.readyCount.value = session?.readyCount ?? 0;
    this.state.unrecognisedCount.value = session?.unrecognisedCount ?? 0;
    this.state.frontAndBack.value = session?.frontAndBack ?? false;
    this.state.kind.value = session?.kind ?? "auto";
    this.state.heldFront.value = session?.heldFront?.previewUrl ?? null;
    const outcome = session?.awaitingPhoto?.codeOutcome ?? null;
    this.state.codeOutcome.value = outcome;
    // A barcode that named nothing: the next picture goes with it, on its attempt.
    this.state.prompt.value = session?.heldFront
      ? BACK_PROMPT
      : outcome !== null
        ? codeOutcomeText(outcome)
        : FRONT_PROMPT;
  };

  #showCamera(): void {
    const { camera, broadcastStudio } = this.deps;
    this.state.camera.value = camera.phase;
    this.state.torch.value = camera.torch;
    this.state.cameraProblem.value =
      camera.phase === "failed" && camera.failure !== null
        ? CAMERA_SENTENCES[camera.failure]
        : camera.phase === "off" && broadcastStudio.onAir
          ? CAMERA_SENTENCES.onAir
          : null;
  }
}
